import React, { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { CommonModal, ConfimationModal, InstrumentCreate } from "../Modal";
import {
  deleteAxios,
  getAxios,
  patchAxios,
  postAxios,
} from "../Services/commonService";
import {
  API_BADLA_INSTRUMENT_LIST,
  API_BADLA_INSTRUMENT_UPDATE,
  API_BADLA_INSTRUMENT_DELETE,
  API_BADLA_INSTRUMENT_DEFAULTUPDATE,
} from "../utils/api/ApiConstant";
import { DataTable, DisplayOrder, SubHeading } from "../Components";
import Icon from "../assets/icons/Icon";

const BadlaInstrument = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [isShow, setIsShow] = useState({
    isOpen: false,
    rowData: null,
    name: "",
    modalTitle: "",
    modalContent: null,
    apiFlag: false,
  });

  const {
    isLoading,
    data: badlaInstrumentData,
    refetch,
  } = useQuery({
    queryKey: [
      "badlaInstrumentData",
      currentPage,
      recordsPerPage,
      searchTerm,
      isShow.apiFlag,
    ],
    queryFn: async () =>
      await getAxios({
        url: API_BADLA_INSTRUMENT_LIST,
        params: {
          page: currentPage,
          limit: recordsPerPage,
          search: searchTerm,
        },
      }),
  });

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleRecordsperPage = useCallback((value) => {
    setRecordsPerPage(value);
  }, []);

  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "action",
        header: "action",
        cell: ({ row }) => {
          const handleUpdateModal = useCallback(({ rowData }) => {
            setIsShow({
              isOpen: true,
              rowData,
              name: "edit",
              modalTitle: "Edit Instrument",
              modalContent: InstrumentCreate,
            });
          });

          return (
            <div className="d-flex">
              <Icon
                name="MdEdit"
                className="m-1"
                title="Edit"
                onClick={() => handleUpdateModal({ rowData: row?.original })}
              />

              <ConfimationModal
                isIcon={true}
                iconName="MdDelete"
                getValue={row?.original?.isAutomatic}
                onStatusChange={() =>
                  handleDeleteRequest({
                    rowData: row?.original,
                  })
                }
              />
            </div>
          );
        },
      },
      {
        accessorKey: "settingName",
        header: "Badla Name",
      },
      {
        accessorKey: "isActive",
        header: "isActive",
        cell: ({ row }) => (
          <ConfimationModal
            isSwitch={true}
            getValue={row?.original?.isActive}
            onStatusChange={(newValue) =>
              handleUpdateRequest({
                url: API_BADLA_INSTRUMENT_UPDATE,
                rowData: row?.original,
                newValue,
                key: "isActive",
              })
            }
          />
        ),
      },
      {
        accessorKey: "isDefault",
        header: "isDefault",
        cell: ({ row }) => (
          <ConfimationModal
            isSwitch={true}
            getValue={row?.original?.isDefault}
            onStatusChange={(newValue) =>
              handleUpdateRequest({
                url: `${API_BADLA_INSTRUMENT_DEFAULTUPDATE}/${row?.original._id}`,
                rowData: row?.original,
                newValue,
                key: "isDefault",
              })
            }
          />
        ),
      },
      {
        accessorKey: "marketWatch",
        header: "Market Watch",
        cell: ({ row }) => (
          <ConfimationModal
            isSwitch={true}
            getValue={row?.original?.marketWatch}
            onStatusChange={(newValue) =>
              handleUpdateRequest({
                url: API_BADLA_INSTRUMENT_UPDATE,
                rowData: row?.original,
                newValue,
                key: "marketWatch",
              })
            }
          />
        ),
      },
      {
        accessorKey: "displayOrder",
        header: "Display Order",
        cell: ({ row }) => {
          return (
            <div className="action_icon_box">
              <DisplayOrder
                initialValue={row?.original?.displayOrder}
                onConfirm={(newValue) =>
                  handleUpdateRequest({
                    url: API_BADLA_INSTRUMENT_UPDATE,
                    rowData: row?.original,
                    newValue,
                    key: "displayOrder",
                  })
                }
              />
            </div>
          );
        },
      },
      {
        accessorKey: "settingValue",
        header: "Duty %",
        cell: ({ row }) => row?.original?.settingValue?.[0]?.Duty,
      },
      {
        accessorKey: "equation",
        header: "Equation",
      },
      {
        accessorKey: "instrumentsDetail",
        header: "Instruments Detail",
        cell: ({ getValue }) => {
          const customValue = handleInstrumentDetails(getValue());
          return customValue;
        },
      },
      {
        accessorKey: "displayName",
        header: "Display Name",
      },
    ],
    []
  );

  const fetchData = useCallback(() => {
    if (!badlaInstrumentData) return { data: [], pages: 0 };

    return {
      data: badlaInstrumentData.docs,
      pages: badlaInstrumentData.totalPages,
    };
  }, [badlaInstrumentData]);

  const handleInstrumentDetails = useCallback((instrumentDetails) => {
    if (!instrumentDetails) return null;
    const newArr = instrumentDetails.map(
      (val) => `${val?.original_exchange}-${val?.name} [${val?.expiry}]`
    );

    return newArr.join(",");
  }, []);

  const handleQueryClient = (rowData, updatedRowData, key) => {
    return queryClient.setQueryData(
      [
        "badlaInstrumentData",
        currentPage,
        recordsPerPage,
        searchTerm,
        isShow.apiFlag,
      ],
      (oldData) => {
        return {
          ...oldData,
          docs: oldData?.docs.map((obj) =>
            obj._id === rowData._id
              ? { ...obj, [key]: updatedRowData[key] }
              : obj
          ),
        };
      }
    );
  };

  const handleUpdateRequest = async ({ url, rowData, newValue, key }) => {
    const payload = { ...rowData, [key]: newValue };

    const res = await postAxios({ url, payload });

    if (res) {
      refetch();
      // handleQueryClient(rowData, res, key);
    }
  };

  const handleDeleteRequest = async ({ rowData }) => {
    const costumUrl = `${API_BADLA_INSTRUMENT_DELETE}/${rowData?._id}`;

    const res = await deleteAxios({
      url: costumUrl,
      payload: {},
    });

    if (res) {
      setIsShow((prev) => ({
        ...prev,
        apiFlag: !prev.apiFlag,
      }));
    }
  };

  return (
    <>
      <SubHeading
        subTitle="Badla Instrument"
        isAddBtn={true}
        handleShowHide={() =>
          setIsShow((prev) => ({
            ...prev,
            isOpen: true,
            rowData: {},
            name: "add",
            modalTitle: "Add Exchange",
            modalContent: InstrumentCreate,
          }))
        }
      />

      <div className="card-body">
        <DataTable
          columns={columns}
          fetchData={fetchData}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsperPage}
          recordsPerPage={recordsPerPage}
          onSearchChange={handleSearch}
          isLoading={isLoading}
          isIndexing={true}
          isCheckBox={false}
          handleSearchKey={setIsShow}
        />
      </div>

      {isShow.isOpen && (
        <CommonModal
          isShow={isShow.isOpen}
          handleShowHide={() =>
            setIsShow((prev) => ({
              ...prev,
              isOpen: false,
              rowData: {},
              name: "",
            }))
          }
          modalTitle={isShow.modalTitle}
        >
          {isShow.modalContent &&
            React.createElement(isShow.modalContent, {
              handleShowHide: () =>
                setIsShow((prev) => ({
                  ...prev,
                  isOpen: false,
                  rowData: {},
                  name: "",
                  modalTitle: "",
                  apiFlag: !prev.apiFlag,
                })),
              rowData: isShow.rowData,
            })}
        </CommonModal>
      )}
    </>
  );
};

export default BadlaInstrument;
